import { useState } from "react";
import { useSaveMessageCampaign } from "../../../../App/Queries/CampaignQueries";
import { useSendScheduleMessageToApi } from "../../../../App/Queries/MessageQueries";
import { useCurrentUser } from "../../../../App/Queries/UserQueries";
import {
  useMessageScheduleHandlers,
  useMessageScheduleState,
} from "../../../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { useAppSelector } from "../../../../App/State/Hooks/reduxHooks";
import { selectClientValue } from "../../../../App/State/Slices/SelectedClientSlice";
import { ISaveDripCampaignCommand } from "../../../../App/Typings/Campaigns";
import { ICandidate } from "../../../../App/Typings/Candidate";

export default function CreateCampaignModalState() {
  const steps = ["Campaign Information", "Candidates", "Email Information", "Review"];

  const [selectedCandidates, setSelectedCandidates] = useState<ICandidate[]>([]);
  const [candidateListLoading, setIsCandidateListLoading] = useState<boolean>(false);

  const { id, name } = useCurrentUser();
  const { clientId } = useAppSelector(selectClientValue);
  const { mutate: sendMessagesToQueue } = useSendScheduleMessageToApi();

  const {
    mutate: saveMessageCampaign,
    isLoading: saveCampaignLoading,
    isSuccess: saveSuccess,
    isError: saveError,
  } = useSaveMessageCampaign();
  const { msgListQueue } = useMessageScheduleState();
  const { handleClearMessageQueue } = useMessageScheduleHandlers();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [campaign, setCampaign] = useState<ISaveDripCampaignCommand>({
    userId: id,
    clientId: clientId,
    startDate: new Date(),
    ongoing: true,
    endDate: new Date("12/31/2099"),
  } as ISaveDripCampaignCommand);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      if (prevActiveStep === 1) {
        const newCampaign = { ...campaign };
        newCampaign.candidateIds = selectedCandidates.map((c) => c.id);
        setCampaign(newCampaign);
      }
      return prevActiveStep + 1;
    });
  };

  const handleSave = () => {
    saveMessageCampaign(campaign, {
      onSuccess: (campaignId) => {
        sendMessagesToQueue({
          candidates: campaign.candidateIds as string[],
          isEmail: true,
          isGroup: false,
          isText: false,
          messages: msgListQueue,
          userId: id,
          uiComponent: "CreateEmailMessageCampaign",
          userName: name,
          campaignId: campaignId,
          replyMessageId: "",
          notify: false,
          notifyEmail: "",
        });
        resetData();
        handleNext();
      },
    });
  };

  const handleReset = () => {
    resetData();
    setActiveStep(0);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCampaignNameChange = (val: string) => setCampaign((campaign) => ({ ...campaign, name: val }));

  const handleCandidatesChange = (candidates: ICandidate[]) => {
    setSelectedCandidates(candidates);
  };

  const handleCandidatesLoading = (isLoading: boolean) => {
    setIsCandidateListLoading(isLoading);
  };

  const canAdvancedToNextStep = () => {
    switch (activeStep) {
      case 0:
        return !!campaign.name;
      case 1:
        return selectedCandidates.length > 0;
      case 2:
        return msgListQueue.length > 0;
    }
    return false;
  };

  const resetData = () => {
    setCampaign({
      userId: id,
      clientId: clientId,
      startDate: new Date(),
      ongoing: true,
      endDate: new Date("12/31/2099"),
      candidateIds: [],
      name: "",
    } as ISaveDripCampaignCommand);
    handleClearMessageQueue();
    setSelectedCandidates([]);
  };

  return {
    getters: {
      steps,
      campaign,
      activeStep,
      selectedCandidates,
      canAdvancedToNextStep: canAdvancedToNextStep(),
      isCampaignSaved: !saveError && saveSuccess,
      saveCampaignLoading: saveCampaignLoading,
      messages: msgListQueue,
      candidateListLoading,
    },
    effects: {
      handleCampaignNameChange,
      handleNext,
      handleBack,
      handleSave,
      handleReset,
      handleCandidatesChange,
      handleCandidatesLoading,
    },
  };
}
